import { localDateKey, todayKey } from '../../utils/datetime';
import { LEAVE_TYPE_LABELS, resolveLeaveType, unavailabilityLabel } from '../../utils/leaveTypes';
import React, {
    useState, useEffect, useMemo, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth } from '../../context/AuthContext';
import { useLiveRefresh } from '../../context/LiveUpdatesContext';
import { Loader } from '../../components';
import { dutyRosterService, unavailabilityService, staffService } from '../../services';
import { toast } from 'react-hot-toast';
import { enqueueChange as enqueueChangeAction, removeChange, setChanges, clearChanges } from '../../store/rosterDraftSlice';
import { applyDraft } from './duty_roster/applyDraft';
import { InitialsAvatar, hhmm } from './duty_roster/shared';
import { DutyRosterProvider } from './duty_roster/DutyRosterContext';
import RosterHeader from './duty_roster/RosterHeader';
import RosterGrid from './duty_roster/RosterGrid';
import EmployeeDetailsModal from './duty_roster/modals/EmployeeDetailsModal';
import ShiftDetailsModal from './duty_roster/modals/ShiftDetailsModal';
import ReviewModal from './duty_roster/modals/ReviewModal';
import ReviewListModal from './duty_roster/modals/ReviewListModal';
import ManageTeamsModal from './duty_roster/modals/ManageTeamsModal';
import AddTeamModal from './duty_roster/modals/AddTeamModal';
import CoverModal from './duty_roster/modals/CoverModal';
import RenameTeamModal from './duty_roster/modals/RenameTeamModal';
import ExportPayrollModal from './duty_roster/modals/ExportPayrollModal';
import PublishReviewModal from './duty_roster/modals/PublishReviewModal';

const DutyRoster = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isDomiciliaryManager =
        user?.role?.role_code === 'domiciliary_manager' ||
        user?.organization?.organization_type === 'domiciliary';

    // Logic & State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [weekDates, setWeekDates] = useState([]);
    // The roster is edited as a DRAFT: server* holds the saved snapshot from the
    // API, `changes` (Redux) holds the manager's unpublished edits, and the
    // groups / employees the whole page reads are the snapshot with the draft
    // applied on top. Nothing hits the API until Publish.
    const dispatch = useDispatch();
    const changes = useSelector((s) => s.rosterDraft.changes);
    const [serverGroups, setServerGroups] = useState([]);
    const [serverEmployees, setServerEmployees] = useState([]);
    const { groups, employees: employeesWithRoster } = useMemo(
        () => applyDraft(serverGroups, serverEmployees, changes),
        [serverGroups, serverEmployees, changes]
    );
    const allEmployees = employeesWithRoster;
    // Draft-changes review modal (opened from the unpublished-changes badge).
    const [draftModalOpen, setDraftModalOpen] = useState(false);
    // Publish-review modal (opened from the Publish button) — shows what's about
    // to be synced, then confirms.
    const [publishModalOpen, setPublishModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [publishLoading, setPublishLoading] = useState(false);
    const [isShiftDetailsModalOpen, setIsShiftDetailsModalOpen] = useState(false);
    const [rosterView, setRosterView] = useState('weekly'); // 'weekly' or 'daily'
    // Day selected within the visible week when rosterView === 'daily'.
    const [dailyDate, setDailyDate] = useState(new Date());
    const [selectedShiftDetail, setSelectedShiftDetail] = useState(null);
    // Employee details modal (opens when the manager clicks an employee tile —
    // available for every employee, not just those who've clocked in). Shows
    // the carer's roster + lets the manager add / remove holiday windows.
    const [employeeModal, setEmployeeModal] = useState(null); // { emp, date } or null
    const [employeeUnavailability, setEmployeeUnavailability] = useState([]);
    const [unavailabilityLoading, setUnavailabilityLoading] = useState(false);
    const [unavailForm, setUnavailForm] = useState({ start_date: '', end_date: '', leave_type: 'annual', notes: '' });
    const [unavailSaving, setUnavailSaving] = useState(false);
    // Header search filters the visible employee rows; teams can be collapsed
    // in the week view; the colour-key popover explains the shift tones.
    const [searchTerm, setSearchTerm] = useState('');
    const [collapsedTeams, setCollapsedTeams] = useState(() => new Set());
    const [colourKeyOpen, setColourKeyOpen] = useState(false);

    // Export payroll-hours modal — turns the roster's matched / approved actual
    // hours into a payroll-ready timesheet the manager can download as CSV.
    // exportPeriod scopes to the selected day or the whole visible week;
    // exportEmployeeId narrows to one carer or 'all'.
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportPeriod, setExportPeriod] = useState('day'); // 'day' | 'week' | 'fourweek' | 'month'
    const [exportEmployeeId, setExportEmployeeId] = useState('all'); // 'all' | userId
    // For multi-week periods (4-week / month) the loaded week isn't enough, so we
    // fetch the spanning weeks and merge them here. null = use the loaded data.
    const [exportEmployees, setExportEmployees] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    const openExportModal = () => {
        setColourKeyOpen(false);
        // Default the period to whichever view the manager is looking at.
        setExportPeriod(rosterView === 'weekly' ? 'week' : 'day');
        setExportEmployeeId('all');
        setExportModalOpen(true);
    };

    // "Manage teams" modal — list every team with its staff and move carers
    // between teams. Staff (with their current team) load lazily on open.
    const [manageTeamsOpen, setManageTeamsOpen] = useState(false);
    const [manageStaff, setManageStaff] = useState([]);   // [{id,name,user_id,group_id,group_name,shift_name}]
    const [manageMoves, setManageMoves] = useState({});   // userId -> target group_id (number) or '' (unassigned)
    const [manageLoading, setManageLoading] = useState(false);
    const [manageSearch, setManageSearch] = useState('');
    const [savingMoves, setSavingMoves] = useState(false);
    const [draggedMemberId, setDraggedMemberId] = useState(null); // carer being dragged
    const [dragOverTeam, setDragOverTeam] = useState(undefined);  // team bucket under cursor ('' = Unassigned)

    const openManageTeams = async () => {
        setColourKeyOpen(false);
        setManageTeamsOpen(true);
        setManageSearch('');
        setManageStaff([]);
        setManageMoves({});
        try {
            setManageLoading(true);
            const res = await dutyRosterService.getTeamOptions();
            if (res.success) {
                const staff = res.data?.staff || [];
                setManageStaff(staff);
                const moves = {};
                staff.forEach((s) => { moves[s.id] = s.group_id || ''; });
                // Fold in any unpublished team moves so the modal opens showing
                // the draft state rather than the stale saved teams.
                changes
                    .filter((c) => c.type === 'reassign')
                    .forEach((c) => (c.payload?.assignments || []).forEach((a) => {
                        if (Object.prototype.hasOwnProperty.call(moves, a.user_id)) {
                            moves[a.user_id] = a.group_id == null ? '' : a.group_id;
                        }
                    }));
                setManageMoves(moves);
            }
        } catch (err) {
            toast.error('Could not load teams & staff');
        } finally {
            setManageLoading(false);
        }
    };

    const closeManageTeams = () => {
        if (savingMoves) return;
        setManageTeamsOpen(false);
    };

    const setMemberTeam = (userId, groupId) => {
        setManageMoves((prev) => ({ ...prev, [userId]: groupId }));
    };

    // ── Draft queue helpers ──────────────────────────────────────────────────
    // Every rota edit is appended to the Redux draft queue instead of calling
    // the API. The grid reflects it immediately (applyDraft) and Publish later
    // replays the queue. dedupKey keeps a single entry when the same target is
    // edited again (e.g. re-saving a cover for the same carer + date).
    const draftId = () => `chg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const enqueueChange = (change) => dispatch(enqueueChangeAction({ id: draftId(), ...change }));

    const submitManageTeams = async () => {
        // Only send carers whose team actually changed.
        const assignments = manageStaff
            .filter((s) => String(manageMoves[s.id] ?? '') !== String(s.group_id ?? ''))
            .map((s) => ({ user_id: s.id, group_id: manageMoves[s.id] === '' ? null : Number(manageMoves[s.id]) }));
        if (assignments.length === 0) {
            toast('No changes to save');
            setManageTeamsOpen(false);
            return;
        }
        // Stage the moves as a draft change (synced on Publish).
        enqueueChange({
            type: 'reassign',
            payload: { assignments },
            label: `Move ${assignments.length} staff between teams`,
        });
        toast.success('Team changes added to draft');
        setManageTeamsOpen(false);
    };

    // Cover modal — assign a carer to work a day they'd otherwise be off / on
    // holiday. Two ways in:
    //   'card' — tap a carer's rest card (carer + date are fixed)
    //   'pick' — the header "Cover" button (choose the carer + date directly)
    // Times pre-fill from the carer's daily roster; the manager can change them.
    const [coverModal, setCoverModal] = useState(null); // { mode, emp?, date? } | null
    const [coverForm, setCoverForm] = useState({ employee_id: '', date: '', start_time: '08:00', end_time: '16:00', note: '' });
    const [coverStaff, setCoverStaff] = useState([]);
    const [coverStaffLoading, setCoverStaffLoading] = useState(false);
    const [savingCover, setSavingCover] = useState(false);

    const rosterTimes = (emp) => {
        const r = emp?.rosterDetails;
        return {
            start: r?.shift_start ? String(r.shift_start).slice(0, 5) : '08:00',
            end: r?.shift_end ? String(r.shift_end).slice(0, 5) : '16:00',
        };
    };

    const openCoverForOff = (emp, date, existing) => {
        // Editing an existing cover prefills its times; a new one uses the
        // carer's daily roster times as the starting point.
        const { start, end } = rosterTimes(emp);
        setCoverForm({
            employee_id: String(emp.id),
            date: localDateKey(date),
            start_time: existing?.start_time ? (fmtClock(existing.start_time) || start) : start,
            end_time: existing?.end_time ? (fmtClock(existing.end_time) || end) : end,
            note: existing?.notes || '',
        });
        setCoverModal({ mode: 'card', emp, date });
    };

    // Per-team "Cover" button — pick the carer + date directly. The carer list
    // is scoped to that team's own staff (a carer belongs to one team).
    const openCoverPicker = async (group) => {
        const defaultDate = localDateKey(dailyDate || new Date());
        setCoverForm({ employee_id: '', date: defaultDate, start_time: '08:00', end_time: '16:00', note: '' });
        setCoverStaff([]);
        setCoverModal({ mode: 'pick', group });
        try {
            setCoverStaffLoading(true);
            const res = await dutyRosterService.getTeamOptions();
            if (res.success) {
                const staff = res.data?.staff || [];
                setCoverStaff(staff);
                const scoped = group ? staff.filter((s) => s.group_id === group.id) : staff;
                if (scoped.length) {
                    const first = scoped[0];
                    const emp = employeesWithRoster.find((e) => e.id === first.id);
                    const { start, end } = rosterTimes(emp);
                    setCoverForm((f) => ({ ...f, employee_id: String(first.id), start_time: start, end_time: end }));
                }
            }
        } catch (err) {
            toast.error('Could not load staff');
        } finally {
            setCoverStaffLoading(false);
        }
    };

    // When the carer changes in pick mode, re-default the times to their roster.
    const onCoverEmployeeChange = (id) => {
        const emp = employeesWithRoster.find((e) => String(e.id) === String(id));
        const { start, end } = rosterTimes(emp);
        setCoverForm((f) => ({ ...f, employee_id: id, start_time: start, end_time: end }));
    };

    const closeCover = () => {
        if (savingCover) return;
        setCoverModal(null);
    };

    const submitCover = async () => {
        if (!coverModal) return;
        if (!coverForm.employee_id) { toast.error('Pick a staff member'); return; }
        if (!coverForm.date) { toast.error('Pick a date'); return; }
        if (!coverForm.start_time || !coverForm.end_time) { toast.error('Pick start and end time'); return; }
        const empId = Number(coverForm.employee_id);
        const empName = employeesWithRoster.find((e) => e.id === empId)?.name || 'Staff';
        // Stage the cover as a draft change (synced on Publish). One entry per
        // carer + date, so re-editing the same cover replaces it.
        enqueueChange({
            type: 'cover',
            payload: {
                employee_id: empId,
                date: coverForm.date,
                start_time: coverForm.start_time,
                end_time: coverForm.end_time,
                note: coverForm.note || null,
            },
            dedupKey: `cover:${empId}:${coverForm.date}`,
            label: `Cover · ${empName} · ${coverForm.date} (${coverForm.start_time}–${coverForm.end_time})`,
        });
        toast.success('Cover added to draft');
        setCoverModal(null);
    };

    // Edit-team modal — the gear icon next to a team name opens this. Edits the
    // name + manager and shows the team's current members.
    const [renameModal, setRenameModal] = useState(null); // { id, name, manager_id } | null
    const [editManagers, setEditManagers] = useState([]); // managers to choose from
    const [editMembers, setEditMembers] = useState([]);   // this team's staff (read-only list)
    const [editLoading, setEditLoading] = useState(false);
    const [savingRename, setSavingRename] = useState(false);

    const openRenameTeam = async (group) => {
        setRenameModal({ id: group.id, name: group.name || '', manager_id: group.manager_id ? String(group.manager_id) : '' });
        setEditManagers([]);
        setEditMembers([]);
        try {
            setEditLoading(true);
            const res = await dutyRosterService.getTeamOptions();
            if (res.success) {
                setEditManagers(res.data?.managers || []);
                setEditMembers((res.data?.staff || []).filter((s) => s.group_id === group.id));
            }
        } catch (err) {
            toast.error('Could not load managers & members');
        } finally {
            setEditLoading(false);
        }
    };
    const closeRenameTeam = () => { if (!savingRename) setRenameModal(null); };

    const submitRenameTeam = async () => {
        if (!renameModal) return;
        if (!renameModal.name.trim()) { toast.error('Enter a team name'); return; }
        const managerId = renameModal.manager_id || null;
        const managerName = managerId
            ? (editManagers.find((m) => String(m.id) === String(managerId))?.name || null)
            : null;
        // Stage the rename / manager change as a draft (synced on Publish).
        enqueueChange({
            type: 'rename',
            payload: { teamId: renameModal.id, name: renameModal.name.trim(), manager_id: managerId, manager_name: managerName },
            dedupKey: `rename:${renameModal.id}`,
            label: `Rename team → "${renameModal.name.trim()}"`,
        });
        toast.success('Team change added to draft');
        setRenameModal(null);
    };

    /**
     * Delete a team — immediately (like Add team), after the caller confirms.
     * Its staff go to Unassigned on the server. Draft edits that point at it are
     * cleaned up so Publish can't fail on a team that no longer exists: a
     * pending rename is dropped, a pending move INTO it becomes Unassigned.
     * Throws on failure so the confirm button can say so.
     */
    const deleteTeam = async (team) => {
        const id = Number(team.id);
        const res = await dutyRosterService.deleteTeam(id);
        const cleaned = changes
            .filter((c) => !(c.type === 'rename' && Number(c.payload?.teamId) === id))
            .map((c) => (c.type === 'reassign'
                ? {
                    ...c,
                    payload: {
                        ...c.payload,
                        assignments: (c.payload?.assignments || []).map((a) => (Number(a.group_id) === id ? { ...a, group_id: null } : a)),
                    },
                }
                : c));
        if (JSON.stringify(cleaned) !== JSON.stringify(changes)) dispatch(setChanges(cleaned));
        // An open Manage teams window shows the team's people under Unassigned.
        setManageStaff((prev) => prev.map((s) => (Number(s.group_id) === id ? { ...s, group_id: null, group_name: null } : s)));
        setManageMoves((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, String(v) === String(id) ? '' : v])));
        setCollapsedTeams((prev) => { const next = new Set(prev); next.delete(team.id); return next; });
        toast.success(res?.message || 'Team deleted');
        await refreshRoster();
    };

    const toggleTeamCollapse = (id) => {
        setCollapsedTeams((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    // Review modal — opened from the "Review" chip on a missed / differs card.
    const [reviewModal, setReviewModal] = useState(null); // { kind, emp, date, dayShift, group }
    const [manualForm, setManualForm] = useState({ clockIn: '', clockOut: '', note: '' });
    const [absenceType, setAbsenceType] = useState(null);
    const [absenceNote, setAbsenceNote] = useState('');
    const [reviewSaving, setReviewSaving] = useState(false);
    // Review-list modal — opened from a "Review" pill (banner or team header).
    const [reviewListOpen, setReviewListOpen] = useState(null); // { scope, group } | null

    // "Jump to Now" — snap the visible week to today's week and select today
    // inside the day pills. Keep the current view (weekly / daily) so the
    // manager isn't yanked into a different layout.
    const jumpToNow = () => {
        const now = new Date();
        setCurrentDate(now);
        setDailyDate(now);
    };

    useEffect(() => {
        const start = startOfWeek(currentDate);
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const nextDate = new Date(start);
            nextDate.setDate(start.getDate() + i);
            dates.push(nextDate);
        }
        setWeekDates(dates);

        // Keep the daily-view selection inside the visible week.
        const startStr = localDateKey(start);
        const endDateObj = new Date(start);
        endDateObj.setDate(start.getDate() + 6);
        const endStr = localDateKey(endDateObj);
        const dailyStr = localDateKey(dailyDate);
        if (dailyStr < startStr || dailyStr > endStr) {
            const todayStr = todayKey();
            setDailyDate(todayStr >= startStr && todayStr <= endStr ? new Date() : new Date(start));
        }

        const dateStr = localDateKey(currentDate);
        fetchRoster(dateStr, rosterView);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, rosterView]);

    // Draft changes live only in memory. Warn before a refresh / tab close /
    // leaving the site while there are unpublished changes — confirming the
    // browser prompt reloads the page and clears the draft.
    useEffect(() => {
        if (changes.length === 0) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [changes.length]);

    const startOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    };

    const formatDateObj = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
        return {
            monthDate: `${months[date.getMonth()]} ${date.getDate()}`,
            dayExt: `(${dayName})`
        };
    };

    // Friendly week label, e.g. "May 18 – 24" or "May 30 – Jun 5".
    // Drops the second month when the range stays inside one month.
    const formatWeekRange = (dates) => {
        if (!dates || dates.length < 7) return '';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const a = dates[0];
        const b = dates[6];
        const aMo = months[a.getMonth()];
        const bMo = months[b.getMonth()];
        if (aMo === bMo) return `${aMo} ${a.getDate()} – ${b.getDate()}`;
        return `${aMo} ${a.getDate()} – ${bMo} ${b.getDate()}`;
    };

    const getDayName = (date) => {
        return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()];
    };

    // `quiet`: a live refresh — no spinner, so the grid, scroll position, open
    // dialogs and the unpublished draft (kept in Redux and re-applied on top by
    // applyDraft) all stay put. Only the newest request may write: a slow
    // background reply must not overwrite a week the manager has moved to.
    const fetchSeq = useRef(0);
    const fetchRoster = async (dateStr, viewType, { quiet = false } = {}) => {
        const seq = ++fetchSeq.current;
        try {
            if (!quiet) setLoading(true);
            const response = await dutyRosterService.getDutyRoster(dateStr, viewType);
            if (seq !== fetchSeq.current) return;
            if (response.success) {
                setServerGroups(response.data.groups || []);
                setServerEmployees(response.data.employees || []);
            }
        } catch (err) {
            console.error('Error fetching roster:', err);
            if (!quiet) toast.error('Failed to load schedule');
        } finally {
            if (!quiet && seq === fetchSeq.current) setLoading(false);
        }
    };

    // Clock-ins from the app, another manager's edits, leave approved
    // elsewhere — the open rota follows them without a reload.
    useLiveRefresh(['rota'], () => fetchRoster(localDateKey(currentDate), rosterView, { quiet: true }));

    // Open the Employee Details modal for any employee row (clicked in either
    // weekly or daily view). Loads their holiday list lazily.
    const openEmployeeModal = async (emp, date) => {
        setEmployeeModal({ emp, date });
        setUnavailForm({ start_date: '', end_date: '', leave_type: 'annual', notes: '' });
        setEmployeeUnavailability([]);
        try {
            setUnavailabilityLoading(true);
            const res = await unavailabilityService.list({ employeeId: emp.id });
            const saved = res.success ? (res.data || []) : [];
            // Show unpublished draft holidays for this carer alongside the saved
            // ones, and hide any saved entry queued for draft removal.
            const removedIds = new Set(
                changes.filter((c) => c.type === 'removeAbsence').map((c) => String(c.payload?.id))
            );
            const draftAdds = changes
                .filter((c) => c.type === 'absence' && Number(c.payload?.employee_id) === emp.id)
                .map((c) => ({ id: c.id, start_date: c.payload.start_date, end_date: c.payload.end_date, reason: c.payload.reason, notes: c.payload.notes, __draft: true }));
            setEmployeeUnavailability([...draftAdds, ...saved.filter((u) => !removedIds.has(String(u.id)))]);
        } catch (err) {
            toast.error('Could not load holiday list');
        } finally {
            setUnavailabilityLoading(false);
        }
    };

    const closeEmployeeModal = () => {
        setEmployeeModal(null);
        setEmployeeUnavailability([]);
        setUnavailForm({ start_date: '', end_date: '', leave_type: 'annual', notes: '' });
    };

    const submitUnavailability = async () => {
        if (!employeeModal) return;
        if (!unavailForm.start_date || !unavailForm.end_date) {
            toast.error('Pick a start and end date');
            return;
        }
        if (unavailForm.start_date > unavailForm.end_date) {
            toast.error('Start date must be before end date');
            return;
        }
        // Stage the holiday as a draft change. Use the change id so the modal's
        // list can show it and remove it again before publishing.
        const id = draftId();
        const leaveType = resolveLeaveType(unavailForm.leave_type) || 'other';
        const reason = LEAVE_TYPE_LABELS[leaveType] || 'Other';
        enqueueChange({
            id,
            type: 'absence',
            payload: {
                employee_id: employeeModal.emp.id,
                start_date: unavailForm.start_date,
                end_date: unavailForm.end_date,
                leave_type: leaveType,
                reason,
                notes: unavailForm.notes || null,
            },
            label: `${reason} · ${employeeModal.emp.name} · ${unavailForm.start_date}–${unavailForm.end_date}`,
        });
        toast.success(`${reason} added to draft`);
        setEmployeeUnavailability((prev) => [
            { id, start_date: unavailForm.start_date, end_date: unavailForm.end_date, leave_type: leaveType, reason, notes: unavailForm.notes || null, __draft: true },
            ...prev,
        ]);
        setUnavailForm({ start_date: '', end_date: '', leave_type: 'annual', notes: '' });
    };

    const removeUnavailability = async (id) => {
        if (!window.confirm('Remove this holiday entry?')) return;
        // A draft-only holiday (never sent to the API) is removed by dropping its
        // queued change; a saved one is removed via a staged removeAbsence change.
        const draftChange = changes.find((c) => c.type === 'absence' && c.id === id);
        if (draftChange) {
            dispatch(removeChange(id));
        } else {
            enqueueChange({
                type: 'removeAbsence',
                payload: { id, employee_id: employeeModal?.emp?.id },
                dedupKey: `removeAbsence:${id}`,
                label: `Remove holiday`,
            });
        }
        toast.success('Holiday removal added to draft');
        setEmployeeUnavailability((prev) => prev.filter((u) => String(u.id) !== String(id)));
    };

    // True when this employee has any unavailability record overlapping the
    // given date. Used to flag tiles in the roster grid.
    const isEmployeeUnavailableOn = (emp, date) => {
        const dStr = date instanceof Date ? localDateKey(date) : String(date);
        const list = emp.unavailability;
        if (!Array.isArray(list)) return null;
        return list.find((u) => dStr >= u.start_date && dStr <= u.end_date) || null;
    };

    // Pre-shift status for a rostered tile that has no clock-in record yet.
    // Before the rostered shift_start arrives the carer simply hasn't clocked in
    // yet ('assigned' → shown as "Not clocked in"); the moment the start time
    // passes with no clock-in it becomes 'missed'. Past days are always 'missed',
    // future days 'assigned'. Returns null when an actual shift exists — the
    // Live/Done badges own that. For domiciliary managers the status is only
    // emitted when the carer has a DomiciliaryAssignment covering this date.
    const CLOCK_IN_GRACE_MIN = 0;
    const getClockInStatus = (emp, date, dayShift) => {
        if (dayShift) return null;
        const roster = emp.rosterDetails;
        if (!roster?.shift_start) return null;

        const dateStr = localDateKey(date);
        const dayName = getDayName(date);

        // Not employed yet — there was no shift to attend, so none was missed.
        if (isBeforeEmployment(emp, dateStr)) return null;

        if (isDomiciliaryManager) {
            const assigns = Array.isArray(emp.domiciliaryAssignments) ? emp.domiciliaryAssignments : [];
            const hasAssignment = assigns.some((a) => {
                const start = a.start_date ? String(a.start_date).split('T')[0] : null;
                const end = a.end_date ? String(a.end_date).split('T')[0] : null;
                if (start && dateStr < start) return false;
                if (end && dateStr > end) return false;
                let pat = a.visit_pattern;
                if (typeof pat === 'string') { try { pat = JSON.parse(pat); } catch (_) { pat = []; } }
                return Array.isArray(pat) && pat.includes(dayName);
            });
            if (!hasAssignment) return null;
        }

        const todayStr = todayKey();
        if (dateStr > todayStr) return 'assigned';
        if (dateStr < todayStr) return 'missed';

        const [sh, sm] = roster.shift_start.split(':').map(Number);
        const shiftStartMin = sh * 60 + sm;
        const now = new Date();
        const nowMin = now.getHours() * 60 + now.getMinutes();
        return nowMin > shiftStartMin + CLOCK_IN_GRACE_MIN ? 'missed' : 'assigned';
    };

    // Compare the actual clock-in against the rostered start to decide whether
    // the carer clocked in on schedule. Returns 'matched' when within the grace
    // window, 'differ' when the actual start drifts from the schedule, or null
    // when there's no clock-in / no roster to compare against. Used by both the
    // weekly and daily views to show the On-time / Differs badge.
    const ATTENDANCE_MATCH_GRACE_MIN = 10;
    const getAttendanceMatch = (emp, dayShift) => {
        if (!dayShift?.start_time) return null;
        const roster = emp.rosterDetails;
        if (!roster?.shift_start) return null;
        const [sh, sm] = roster.shift_start.split(':').map(Number);
        const schedStartMin = sh * 60 + sm;
        const a = new Date(dayShift.start_time);
        const actualStartMin = a.getHours() * 60 + a.getMinutes();
        return Math.abs(actualStartMin - schedStartMin) <= ATTENDANCE_MATCH_GRACE_MIN
            ? 'matched'
            : 'differ';
    };

    // Replay a single queued change against the matching API endpoint.
    const syncChange = (c) => {
        const p = c.payload || {};
        switch (c.type) {
            case 'cover': return dutyRosterService.createCover(p);
            case 'reassign': return dutyRosterService.reassignMembers(p);
            case 'rename': return dutyRosterService.renameTeam(p.teamId, { name: p.name, manager_id: p.manager_id });
            case 'removeDay': return dutyRosterService.removeAssignment(p.userId, p.dayName);
            case 'review': return dutyRosterService.recordManualShift(p);
            case 'absence': return unavailabilityService.create(p);
            case 'removeAbsence': return unavailabilityService.remove(p.id);
            default: return Promise.resolve();
        }
    };

    // Publish = sync the whole draft queue to the API, in order. Each change is
    // tried independently; the ones that fail stay queued so nothing is lost,
    // and the manager is told exactly what synced. Then we reload the saved
    // roster as the new snapshot.
    const handlePublish = async () => {
        if (changes.length === 0) { toast('No changes to publish'); return; }
        setPublishLoading(true);
        const total = changes.length;
        const failed = [];
        for (const c of changes) {
            try {
                const res = await syncChange(c);
                if (res && res.success === false) failed.push(c);
            } catch (err) {
                failed.push(c);
            }
        }
        try { await dutyRosterService.publishSchedule(); } catch (_) { /* notify step is best-effort */ }

        if (failed.length === 0) {
            dispatch(clearChanges());
            toast.success(`Published ${total} change${total === 1 ? '' : 's'}`);
            // Everything synced — close the review / draft modals.
            setPublishModalOpen(false);
            setDraftModalOpen(false);
        } else {
            dispatch(setChanges(failed));
            toast.error(`${total - failed.length} of ${total} published — ${failed.length} failed and stay in draft`);
        }
        await fetchRoster(localDateKey(currentDate), rosterView);
        setPublishLoading(false);
    };

    // Remove a single queued change (from the draft-changes list).
    const removeOneChange = (id) => dispatch(removeChange(id));

    // Discard every unpublished change and snap back to the saved roster.
    const handleDiscard = () => {
        if (changes.length === 0) return;
        if (!window.confirm(`Discard ${changes.length} unpublished change${changes.length === 1 ? '' : 's'}?`)) return;
        dispatch(clearChanges());
        setDraftModalOpen(false);
        toast.success('Unpublished changes discarded');
    };

    const removeDay = async (userId, dayName) => {
        if (!window.confirm(`Remove ${dayName} from this employee's roster?`)) return;
        // Stage the day removal as a draft change (synced on Publish).
        const empName = employeesWithRoster.find((e) => e.id === Number(userId))?.name || 'Staff';
        enqueueChange({
            type: 'removeDay',
            payload: { userId, dayName },
            dedupKey: `removeDay:${userId}:${dayName}`,
            label: `Remove ${dayName} · ${empName}`,
        });
        toast.success('Day removal added to draft');
    };


    // ── Prototype-design helpers (Tailwind) ──────────────────────────────────
    // The roster is drawn as team sections with one row per employee — week =
    // 7 day columns, day = a 24-hour timeline — matching the Schedule prototype.

    // Per-status Tailwind tone (card colour / left accent / badge / text).
    // Dark mode keeps the same hue but flips to a low-alpha tint on the dark
    // surface with a light text colour — the light pastels are unreadable and
    // glaring against the dark page.
    const TONE = {
        scheduled:   { card: 'bg-blue-50 border-blue-200 border-l-blue-500 dark:bg-blue-500/10 dark:border-blue-500/25 dark:border-l-blue-500',                text: 'text-blue-900 dark:text-blue-100',       badge: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200' },
        live:        { card: 'bg-cyan-50 border-cyan-200 border-l-cyan-500 dark:bg-cyan-500/10 dark:border-cyan-500/25 dark:border-l-cyan-500',                text: 'text-cyan-900 dark:text-cyan-100',       badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-200' },
        done:        { card: 'bg-emerald-50 border-emerald-200 border-l-emerald-500 dark:bg-emerald-500/10 dark:border-emerald-500/25 dark:border-l-emerald-500', text: 'text-emerald-900 dark:text-emerald-100', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200' },
        differs:     { card: 'bg-amber-50 border-amber-200 border-l-amber-500 dark:bg-amber-500/10 dark:border-amber-500/25 dark:border-l-amber-500',          text: 'text-amber-900 dark:text-amber-100',     badge: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200' },
        missed:      { card: 'bg-rose-50 border-rose-200 border-l-rose-500 dark:bg-rose-500/10 dark:border-rose-500/25 dark:border-l-rose-500',                text: 'text-rose-900 dark:text-rose-100',       badge: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200' },
        off:         { card: 'bg-slate-50 border-slate-200 border-l-slate-400 dark:bg-white/[0.04] dark:border-gray-700 dark:border-l-slate-500',              text: 'text-slate-500 dark:text-gray-400',      badge: 'bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-gray-400' },
        unavailable: { card: 'bg-sky-50 border-sky-200 border-l-sky-500 dark:bg-sky-500/10 dark:border-sky-500/25 dark:border-l-sky-500',                      text: 'text-sky-900 dark:text-sky-100',         badge: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200' },
    };

    const TEAM_COLORS = ['#2563eb', '#0bb77b', '#e3980f', '#6557d9', '#1287a5', '#e83652', '#728da8'];
    const teamColor = (group, idx) => group.color || TEAM_COLORS[idx % TEAM_COLORS.length];

    // working_days for an employee (stored as a JSON string or array).
    const parseDays = (emp) => {
        let d = emp?.rosterDetails?.working_days;
        if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = []; } }
        return Array.isArray(d) ? d : [];
    };

    // Is this date before the employee even joined? A roster pattern is only a
    // set of weekdays with no start of its own, so without this the grid applies
    // it backwards through history and flags every past rostered day as a missed
    // shift — for someone who wasn't employed yet. No start date on file means
    // no filtering, so existing staff are unaffected.
    const isBeforeEmployment = (emp, dateStr) => {
        const start = emp?.userDetails?.start_date;
        if (!start) return false;
        return dateStr < String(start).split('T')[0];
    };

    // Employees rostered to a team, filtered by the header search box.
    const teamStaff = (group) =>
        employeesWithRoster
            .filter((e) => e.rosterDetails?.group_id === group.id)
            .filter((e) => e.name.toLowerCase().includes(searchTerm.toLowerCase().trim()));


    const findDayShift = (emp, dateStr) =>
        emp.shifts?.find((s) => localDateKey(s.start_time) === dateStr);

    const fmtClock = (iso) =>
        iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : null;

    // Resolve { kind, tone, label } for a working day. tone keys into TONE.
    const getCellStatus = (emp, date, dayShift) => {
        // A scheduled cover (manager put them on an otherwise off / holiday day)
        // shows as a normal scheduled shift and overrides the rest / holiday.
        if (dayShift?.status === 'scheduled') return { kind: 'assigned', tone: 'scheduled', label: 'Scheduled' };
        const unavail = isEmployeeUnavailableOn(emp, date);
        if (unavail) return { kind: 'unavailable', tone: 'unavailable', label: unavailabilityLabel(unavail) || 'Unavailable', unavail };
        const isOngoing = dayShift && !dayShift.end_time;
        const isCompleted = dayShift && dayShift.end_time;
        if (isOngoing) return { kind: 'live', tone: 'live', label: 'In progress' };
        if (isCompleted) {
            // Manager already reviewed and approved these hours — the shift is
            // settled, so stop re-deriving "Differs" from the clock-in gap.
            // Without this the Review button came back on every reload.
            if (dayShift.hours_approved_at) return { kind: 'done', tone: 'done', label: 'Approved' };
            const m = getAttendanceMatch(emp, dayShift);
            if (m === 'differ') return { kind: 'differs', tone: 'differs', label: 'Differs' };
            return { kind: 'done', tone: 'done', label: 'Done' };
        }
        const cis = getClockInStatus(emp, date, dayShift);
        if (cis === 'missed') return { kind: 'missed', tone: 'missed', label: 'Missed' };
        return { kind: 'assigned', tone: 'scheduled', label: 'Scheduled' };
    };

    // Open the right modal for a clicked cell/bar.
    const openCell = (emp, date, dayShift, working, dayName, group) => {
        // Scheduled cover → re-open the cover modal to edit it.
        if (dayShift?.status === 'scheduled') {
            openCoverForOff(emp, date, dayShift);
        } else if (!working) {
            // Off / holiday day → offer to assign them as cover for this date.
            openCoverForOff(emp, date);
        } else if (dayShift) {
            setSelectedShiftDetail({ emp, dayShift, date });
            setIsShiftDetailsModalOpen(true);
        } else {
            openEmployeeModal(emp, date);
        }
    };

    // Day-view timeline geometry (24h × 72px columns after a 116px name column).
    const EMP_COL = 116;
    const HOUR_PX = 72;
    const CARD_GAP = 8;
    const BOARD_W = EMP_COL + 24 * HOUR_PX; // 1844
    const hourLabels = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
    const laneBox = (startMin, endMin, minWidth = 150) => {
        const visStart = Math.max(startMin, 0);
        const visEnd = Math.min(endMin, 1440);
        const left = EMP_COL + (visStart / 60) * HOUR_PX;
        const width = Math.max(minWidth, ((visEnd - visStart) / 60) * HOUR_PX - CARD_GAP);
        return { left, width };
    };
    const shortDays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    // Count differs/missed shifts in the given scope so the banner / team
    // headers can show a "needs review" tally (informational, no extra backend).
    // Collect the differs/missed shifts that need review in the given scope.
    // Pass a group to scope to one team, or null for every team.
    const collectReviews = (scope, group) => {
        const teams = group ? [group] : groups;
        const dates = scope === 'week' ? weekDates : [dailyDate];
        const items = [];
        teams.forEach((g) => {
            teamStaff(g).forEach((emp) => {
                dates.forEach((date) => {
                    if (!parseDays(emp).includes(getDayName(date))) return;
                    const dateStr = localDateKey(date);
                    // Days before they joined aren't reviewable — nothing to miss.
                    if (isBeforeEmployment(emp, dateStr)) return;
                    const ds = findDayShift(emp, dateStr);
                    const st = getCellStatus(emp, date, ds);
                    if (st.kind === 'differs' || st.kind === 'missed') {
                        items.push({ emp, date, dayShift: st.kind === 'missed' ? null : ds, kind: st.kind, group: g });
                    }
                });
            });
        });
        return items;
    };
    const reviewCountForTeam = (group, scope) => collectReviews(scope, group).length;

    // One week-view cell (shift card / rest day) for an employee on a date.
    const renderWeekCell = (emp, date, group) => {
        const dayName = getDayName(date);
        const dateStr = localDateKey(date);
        const isToday = dateStr === todayKey();
        // The roster pattern only applies from the employee's start date. A real
        // recorded shift still shows, so nothing historic is ever hidden.
        const isWorkingDay = parseDays(emp).includes(dayName)
            && !isBeforeEmployment(emp, dateStr);
        // Look for a shift on every day — a scheduled cover lives on an
        // otherwise off / holiday day, so it must be found there too.
        const dayShift = findDayShift(emp, dateStr);

        // Before someone joined there is nothing to say about the day — not even
        // "Rest", which would imply they were off when they simply weren't
        // employed. Render an empty cell. A real recorded shift still wins, so
        // genuine history is never hidden.
        if (isBeforeEmployment(emp, dateStr) && !dayShift) {
            return (
                <div
                    key={dateStr}
                    className={`px-2 py-2.5 ${isToday ? 'bg-blue-50/40 dark:bg-blue-500/[0.07]' : 'bg-white dark:bg-[#262b33]'} min-w-0`}
                >
                    <div className="min-h-[98px]" />
                </div>
            );
        }

        const isCover = dayShift?.status === 'scheduled';
        const working = isWorkingDay || !!dayShift;
        const roster = emp.rosterDetails;
        // For a cover the scheduled window is the cover's own times.
        const schedStart = isCover ? fmtClock(dayShift.start_time) : hhmm(roster?.shift_start);
        const schedEnd = isCover ? fmtClock(dayShift.end_time) : hhmm(roster?.shift_end);
        const actualStart = fmtClock(dayShift?.start_time);
        const actualEnd = dayShift?.end_time ? fmtClock(dayShift.end_time) : null;

        // Card title is just "Shift" (working) or "Rest" (off / on leave).
        let tone = 'off', label = 'Off', title = 'Rest', schedText = 'No shift';
        let secondLabel = 'Actual', secondText = '—', reviewKind = null;
        if (working) {
            const st = getCellStatus(emp, date, dayShift);
            tone = st.tone; label = st.label;
            // An extra shift the carer requested under Available Shifts.
            title = st.kind === 'unavailable' ? 'Rest' : (dayShift?.open_shift_id ? 'Extra shift' : 'Shift');
            schedText = schedStart && schedEnd ? `${schedStart} – ${schedEnd}` : 'No shift';
            if (isCover) {
                secondText = 'Not clocked in';
            } else if (st.kind === 'missed') {
                secondLabel = 'Reason'; secondText = 'No reason recorded'; reviewKind = 'missed';
            } else if (dayShift) {
                secondText = `${actualStart}${actualEnd ? ` – ${actualEnd}` : ' – …'}`;
                if (st.kind === 'differs') reviewKind = 'differs';
            } else if (st.kind === 'unavailable') {
                secondText = unavailabilityLabel(st.unavail) || 'Unavailable';
            } else {
                secondText = 'Not clocked in';
            }
        }
        const showReview = !!reviewKind;
        // Break / travel are flagged on the cell only while currently active.
        const onBreakNow = !!dayShift?.break_started_at;
        const travellingNow = !onBreakNow && !!dayShift?.travel_started_at;
        const t = TONE[tone];
        // Mark cells touched by an unpublished draft edit (cover / hours / leave).
        const isDraftCell = !!dayShift?.__draft || !!isEmployeeUnavailableOn(emp, date)?.__draft;
        return (
            <div key={dateStr} className={`px-2 py-2.5 ${isToday ? 'bg-blue-50/40 dark:bg-blue-500/[0.07]' : 'bg-white dark:bg-[#262b33]'} min-w-0`}>
                <div
                    onClick={() => openCell(emp, date, dayShift, working, dayName, group)}
                    className={`relative min-h-[98px] rounded-[10px] border border-l-[4px] p-2.5 cursor-pointer transition hover:shadow-md ${t.card} ${t.text} ${showReview ? 'pb-7' : ''} ${isDraftCell ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                >
                    {isDraftCell && <span className="absolute -top-1 -left-1 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white dark:border-[#262b33]" title="Unpublished change" />}
                    <div className="text-[11px] font-bold pr-12 truncate">{title}</div>
                    <span className={`absolute right-1.5 top-1.5 px-1.5 py-0.5 rounded text-[7px] font-extrabold uppercase tracking-wide ${t.badge}`}>{label}</span>
                    <div className="mt-1.5 pt-1.5 border-t border-black/10 dark:border-white/10 space-y-1.5">
                        <div className="leading-tight">
                            <div className="text-[8px] font-extrabold uppercase opacity-60">Scheduled</div>
                            <div className="text-[9px] font-bold truncate">{schedText}</div>
                        </div>
                        <div className="leading-tight">
                            <div className="text-[8px] font-extrabold uppercase opacity-60">{secondLabel}</div>
                            <div className="text-[9px] font-bold truncate">{secondText}</div>
                        </div>
                        {(onBreakNow || travellingNow) && (
                            <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${onBreakNow
                                ? 'bg-[#efeaff] text-[#6545cf] dark:bg-violet-500/20 dark:text-violet-300'
                                : 'bg-[#e7f0fd] text-[#1f6fd0] dark:bg-blue-500/20 dark:text-blue-300'}`}>
                                ● {onBreakNow ? 'On break' : 'Travelling'}
                            </div>
                        )}
                    </div>
                    {showReview && (
                        <button
                            onClick={(e) => { e.stopPropagation(); openReview(emp, date, dayShift, reviewKind, group); }}
                            className="absolute right-1.5 bottom-1.5 h-[22px] px-2 rounded-md border border-amber-400 dark:border-amber-500/50 bg-white/80 dark:bg-amber-500/20 text-amber-700 dark:text-amber-200 text-[7px] font-extrabold uppercase tracking-wide hover:bg-amber-50 dark:hover:bg-amber-500/30"
                        >
                            Review
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // One day-view timeline row (employee + absolutely-positioned shift bars).
    const renderDayRow = (emp, group) => {
        const dateStr = localDateKey(dailyDate);
        const dayName = getDayName(dailyDate);
        // Same rule as the week view — the pattern doesn't apply before they joined.
        const isWorkingDay = parseDays(emp).includes(dayName)
            && !isBeforeEmployment(emp, dateStr);
        // A scheduled cover lives on an off / holiday day, so look on every day.
        const dayShift = findDayShift(emp, dateStr);
        const isCover = dayShift?.status === 'scheduled';
        const working = isWorkingDay || !!dayShift;
        const roster = emp.rosterDetails;
        const unavail = isEmployeeUnavailableOn(emp, dailyDate);
        const cards = [];
        // The scheduled window is the cover's own times, else the roster's.
        const baseStart = isCover ? fmtClock(dayShift.start_time) : roster?.shift_start;
        const baseEnd = isCover ? fmtClock(dayShift.end_time) : roster?.shift_end;

        // Before they joined the lane stays empty — "Rest" would wrongly imply
        // they were off, rather than not yet employed.
        if (isBeforeEmployment(emp, dateStr) && !dayShift) {
            // no cards
        } else if (!working) {
            cards.push(
                <div key="rest" className="absolute top-[34px] h-[54px] rounded-[11px] border-2 border-slate-300 bg-slate-50 text-slate-500 dark:border-gray-700 dark:bg-white/[0.04] dark:text-gray-400 px-3 py-2.5 overflow-hidden"
                    style={{ left: EMP_COL + 8 * HOUR_PX, width: 3 * HOUR_PX - CARD_GAP }}>
                    <strong className="block text-[10px]">Rest</strong>
                    <div className="text-[10px] font-bold mt-1">No scheduled shift</div>
                </div>
            );
        } else if (baseStart && baseEnd) {
            const st = getCellStatus(emp, dailyDate, dayShift);
            const [sh, sm] = String(baseStart).split(':').map(Number);
            const [eh, em] = String(baseEnd).split(':').map(Number);
            let startMin = sh * 60 + sm;
            let endMin = eh * 60 + em;
            const overnight = endMin <= startMin;
            if (overnight) endMin += 1440;
            const sched = laneBox(startMin, endMin);
            const schedRed = st.kind === 'missed';
            cards.push(
                <div key="sched"
                    onClick={() => openCell(emp, dailyDate, dayShift, working, dayName, group)}
                    className={`absolute top-[9px] h-[57px] rounded-[11px] border-2 px-3 py-2 overflow-hidden cursor-pointer ${schedRed
                        ? 'bg-rose-50 border-rose-300 text-rose-900 dark:bg-rose-500/10 dark:border-rose-500/40 dark:text-rose-100 pb-7'
                        : 'bg-blue-50 border-blue-400 text-blue-900 dark:bg-blue-500/10 dark:border-blue-500/40 dark:text-blue-100'}`}
                    style={{ left: sched.left, width: sched.width }}>
                    <div className="flex items-center justify-between gap-1.5">
                        <strong className="text-[12px] truncate">{dayShift?.open_shift_id ? 'Extra shift' : 'Shift'}</strong>
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${schedRed ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200' : TONE[st.tone].badge}`}>{st.label}</span>
                    </div>
                    <div className="text-[10px] font-bold mt-1 whitespace-nowrap">◷ {hhmm(baseStart)} – {hhmm(baseEnd)}{overnight ? ' (+1)' : ''}</div>
                    {schedRed && (
                        <button onClick={(e) => { e.stopPropagation(); openReview(emp, dailyDate, null, 'missed', group); }}
                            className="absolute right-2 bottom-1.5 h-[20px] px-2 rounded-md border border-rose-400 dark:border-rose-500/50 bg-white/80 dark:bg-rose-500/20 text-rose-700 dark:text-rose-200 text-[7px] font-extrabold uppercase">Review</button>
                    )}
                </div>
            );

            if (dayShift && !isCover) {
                const aStart = new Date(dayShift.start_time);
                let asMin = aStart.getHours() * 60 + aStart.getMinutes();
                const aEndDate = dayShift.end_time ? new Date(dayShift.end_time) : new Date();
                let aeMin = aEndDate.getHours() * 60 + aEndDate.getMinutes();
                if (aeMin <= asMin) aeMin += 1440;
                const act = laneBox(asMin, aeMin);
                const actTone = st.kind === 'live' ? 'bg-cyan-50 border-cyan-300 text-cyan-900 dark:bg-cyan-500/10 dark:border-cyan-500/40 dark:text-cyan-100'
                    : st.kind === 'differs' ? 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-500/10 dark:border-amber-500/40 dark:text-amber-100'
                        : 'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-500/10 dark:border-emerald-500/40 dark:text-emerald-100';
                const actBadge = st.kind === 'live' ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-200'
                    : st.kind === 'differs' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200'
                        : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200';
                // A break is the gap between two work sessions, so each gap is
                // drawn as a vertical-line-bounded hatched segment on the actual
                // bar — the manager can see exactly when (and how long) the carer
                // was on break. A break still in progress (break_started_at, no
                // new session yet) runs to "now", and active travel is shown the
                // same way.
                const onBreakNow = !!dayShift.break_started_at;
                const travellingNow = !!dayShift.travel_started_at;
                const winLen = (aeMin - asMin) || 1;
                const segments = [];
                const pushSeg = (startISO, endISO, isBreak, key) => {
                    const sd = new Date(startISO);
                    let sMin = sd.getHours() * 60 + sd.getMinutes();
                    if (sMin < asMin) sMin += 1440;
                    const ed = new Date(endISO);
                    let eMin = ed.getHours() * 60 + ed.getMinutes();
                    if (eMin < asMin) eMin += 1440;
                    if (eMin < sMin) eMin += 1440;
                    const visEnd = Math.min(eMin, aeMin);
                    if (visEnd <= sMin) return;
                    const leftPct = Math.max(0, Math.min(100, ((sMin - asMin) / winLen) * 100));
                    const widthPct = Math.max(1.5, Math.min(100 - leftPct, ((visEnd - sMin) / winLen) * 100));
                    segments.push(
                        <span key={key} title={`${isBreak ? 'Break' : 'Travelling'} ${fmtClock(startISO)} – ${fmtClock(endISO)}`}
                            className="absolute inset-y-0 z-[1] pointer-events-none"
                            style={{
                                left: `${leftPct}%`, width: `${widthPct}%`,
                                borderLeft: `2px solid ${isBreak ? '#6545cf' : '#1f6fd0'}`,
                                borderRight: `2px solid ${isBreak ? '#6545cf' : '#1f6fd0'}`,
                                backgroundImage: isBreak
                                    ? 'repeating-linear-gradient(135deg,#8d73e8 0,#8d73e8 5px,#a995ed 5px,#a995ed 10px)'
                                    : 'repeating-linear-gradient(135deg,#6aa6f5 0,#6aa6f5 5px,#9cc5fb 5px,#9cc5fb 10px)',
                                opacity: 0.85,
                            }} />
                    );
                };
                // Completed breaks = gaps between consecutive work sessions.
                const sortedSessions = (Array.isArray(dayShift.sessions) ? dayShift.sessions : [])
                    .filter((s) => s && s.started_at)
                    .slice()
                    .sort((a, b) => new Date(a.started_at) - new Date(b.started_at));
                for (let i = 0; i < sortedSessions.length - 1; i++) {
                    const gapStart = sortedSessions[i].ended_at;
                    const gapEnd = sortedSessions[i + 1].started_at;
                    if (gapStart && gapEnd) pushSeg(gapStart, gapEnd, true, `brk-${i}`);
                }
                // Break / travel currently in progress → run to now.
                if (onBreakNow) pushSeg(dayShift.break_started_at, new Date().toISOString(), true, 'brk-live');
                if (travellingNow) pushSeg(dayShift.travel_started_at, new Date().toISOString(), false, 'trv-live');
                cards.push(
                    <div key="actual"
                        onClick={() => openCell(emp, dailyDate, dayShift, working, dayName, group)}
                        className={`absolute top-[74px] h-[58px] rounded-[11px] border-2 px-3 py-1.5 overflow-hidden cursor-pointer ${actTone} ${st.kind === 'differs' ? 'pb-7' : ''}`}
                        style={{ left: act.left, width: act.width }}>
                        {segments}
                        <div className="flex items-center justify-between gap-1.5 relative z-[2]">
                            <strong className="text-[10px] truncate">Actual</strong>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase tracking-wide ${actBadge}`}>{st.label}</span>
                        </div>
                        <div className="text-[10px] font-bold mt-0.5 whitespace-nowrap relative z-[2]">◷ {fmtClock(dayShift.start_time)} – {dayShift.end_time ? fmtClock(dayShift.end_time) : '…'}</div>
                        {(onBreakNow || travellingNow) && (
                            <div className={`relative z-[2] mt-0.5 text-[8px] font-extrabold uppercase tracking-wide truncate ${onBreakNow
                                ? 'text-[#6545cf] dark:text-violet-300'
                                : 'text-[#1f6fd0] dark:text-blue-300'}`}>
                                ● {onBreakNow ? 'On break' : 'Travelling'}
                            </div>
                        )}
                        {st.kind === 'differs' && (
                            <button onClick={(e) => { e.stopPropagation(); openReview(emp, dailyDate, dayShift, 'differs', group); }}
                                className="absolute right-2 bottom-1.5 z-[3] h-[20px] px-2 rounded-md border border-amber-400 dark:border-amber-500/50 bg-white/80 dark:bg-amber-500/20 text-amber-700 dark:text-amber-200 text-[7px] font-extrabold uppercase">Review</button>
                        )}
                    </div>
                );
            } else if (unavail) {
                cards.push(
                    <div key="absence" className="absolute top-[76px] h-[55px] rounded-[11px] border-2 border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-100 px-3 py-2 overflow-hidden"
                        style={{ left: sched.left, width: sched.width }}>
                        <strong className="block text-[10px]">{unavailabilityLabel(unavail) || 'Unavailable'}</strong>
                        <div className="text-[9px] font-bold mt-1 truncate">{unavail.notes || 'Not available'}</div>
                    </div>
                );
            }
        }

        return (
            <div key={emp.id} className="relative grid border-t border-[#EDF1F6] dark:border-gray-800"
                style={{ gridTemplateColumns: `${EMP_COL}px repeat(24, ${HOUR_PX}px)`, height: 142 }}>
                <div onClick={() => openEmployeeModal(emp, dailyDate)}
                    className="sticky left-0 z-[11] bg-white dark:bg-[#262b33] flex flex-col items-center justify-center gap-2 px-2 cursor-pointer shadow-[5px_0_10px_rgba(52,70,98,0.07)]">
                    <InitialsAvatar name={emp.name} size="md" />
                    <span className="text-[11px] font-semibold text-[#1E293B] dark:text-gray-200 leading-tight line-clamp-2">{emp.name}</span>
                </div>
                {hourLabels.map((_, i) => <div key={i} className="border-r border-[#EDF1F6] dark:border-gray-800" />)}
                {cards}
            </div>
        );
    };


    // Open the Review modal for a missed / differs card. Pre-fills the editable
    // clock fields with the recorded actual times (differs) so the manager can
    // approve them as-is or correct them.
    const openReview = (emp, date, dayShift, kind, group) => {
        setReviewModal({ kind, emp, date, dayShift, group });
        // No preselection: an absence type goes on someone's leave record, so
        // the manager has to choose it rather than accept whatever was default.
        setAbsenceType(null);
        setAbsenceNote('');
        if (kind === 'differs' && dayShift) {
            setManualForm({
                clockIn: fmtClock(dayShift.start_time) || '',
                clockOut: dayShift.end_time ? fmtClock(dayShift.end_time) : '',
                note: '',
            });
        } else {
            setManualForm({ clockIn: '', clockOut: '', note: '' });
        }
    };
    const closeReview = () => setReviewModal(null);

    const refreshRoster = () => fetchRoster(localDateKey(currentDate), rosterView);

    // "Add team" modal — creates an EmployeeGroup straight away (same endpoint
    // the Staff → Assign Roster modal uses), then reloads the rota so the new
    // empty team shows up. Unlike the other rota edits this is NOT a draft:
    // teams are shared reference data, not part of the week's schedule.
    const [addTeamOpen, setAddTeamOpen] = useState(false);
    const [newTeamName, setNewTeamName] = useState('');
    const [savingTeam, setSavingTeam] = useState(false);

    const openAddTeam = () => {
        setColourKeyOpen(false);
        setNewTeamName('');
        setAddTeamOpen(true);
    };

    const closeAddTeam = () => {
        if (savingTeam) return;
        setAddTeamOpen(false);
    };

    const submitAddTeam = async () => {
        const name = newTeamName.trim();
        if (!name) { toast.error('Please enter a team name'); return; }
        try {
            setSavingTeam(true);
            const res = await staffService.createStaffGroup({ name });
            if (res.success) {
                toast.success('Team created');
                setAddTeamOpen(false);
                setNewTeamName('');
                refreshRoster();
            }
        } catch (err) {
            toast.error(err.message || 'Error creating team');
        } finally {
            setSavingTeam(false);
        }
    };

    // Upsert the carehome shift with the given clock times + manager note.
    // Staged as a draft change (synced on Publish); one entry per carer + date.
    const submitReviewShift = async (clockIn, clockOut, note) => {
        if (!reviewModal) return;
        if (!clockIn || !clockOut) { toast.error('Enter both clock-in and clock-out'); return; }
        const dStr = localDateKey(reviewModal.date);
        enqueueChange({
            type: 'review',
            payload: { employeeId: reviewModal.emp.id, date: dStr, clockIn, clockOut, note: note || null },
            dedupKey: `review:${reviewModal.emp.id}:${dStr}`,
            label: `Hours · ${reviewModal.emp.name} · ${dStr} (${clockIn}–${clockOut})`,
        });
        toast.success('Hours added to draft');
        closeReview();
    };

    // "Edit times" from Shift Attendance Details — lets a manager correct hours
    // that were already approved / published. Same 'review' draft as above, so
    // it syncs on Publish and the API appends it to the shift's change log.
    // A reason is mandatory: it's the note shown against the change.
    const submitShiftEdit = (emp, date, clockIn, clockOut, note) => {
        if (!clockIn || !clockOut) { toast.error('Enter both clock-in and clock-out'); return false; }
        if (!String(note || '').trim()) { toast.error('Add a reason for the change'); return false; }
        const dStr = localDateKey(date);
        enqueueChange({
            type: 'review',
            payload: { employeeId: emp.id, date: dStr, clockIn, clockOut, note: note.trim() },
            dedupKey: `review:${emp.id}:${dStr}`,
            label: `Hours edited · ${emp.name} · ${dStr} (${clockIn}–${clockOut})`,
        });
        toast.success('Time change added to draft — publish to save');
        setIsShiftDetailsModalOpen(false);
        return true;
    };

    // Mark the scheduled day as a (neutral / planned / unplanned) absence. Reuses
    // the existing unavailability store — one single-day window for the date.
    // Staged as a draft change (synced on Publish).
    const saveReviewAbsence = async () => {
        if (!reviewModal) return;
        // The button is disabled until a type is picked; this guards the call
        // itself so nothing can be filed as a bare "Other" by accident.
        if (!absenceType) {
            toast.error('Choose an absence type first');
            return;
        }
        const dStr = localDateKey(reviewModal.date);
        // Labels come from the leave module's list, so an absence marked here
        // reads the same as one booked or approved anywhere else. `leave_type`
        // is what puts the row into the leave summaries — a row carrying only
        // a `reason` label is not counted in them.
        const leaveType = resolveLeaveType(absenceType) || 'other';
        const reason = LEAVE_TYPE_LABELS[leaveType] || 'Other';
        enqueueChange({
            type: 'absence',
            payload: {
                employee_id: reviewModal.emp.id,
                start_date: dStr,
                end_date: dStr,
                reason,
                leave_type: leaveType,
                notes: absenceNote || null,
            },
            dedupKey: `absence:${reviewModal.emp.id}:${dStr}`,
            label: `Absence · ${reviewModal.emp.name} · ${dStr} (${reason})`,
        });
        toast.success('Absence added to draft');
        closeReview();
    };

    // ── Payroll export ────────────────────────────────────────────────────────
    // A carer reference is synthesised from the user id (no dedicated field on
    // the model) so payroll software has a stable per-employee key.
    const EXPORT_PAY_CODE = 'REG';
    const empRef = (emp) => `EMP-${String(emp.id).padStart(3, '0')}`;
    const fmtUKDate = (date) => {
        const d = date instanceof Date ? date : new Date(date);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        return `${dd}/${mm}/${d.getFullYear()}`;
    };
    const fmtHours = (h) => `${Number(h || 0).toFixed(2)}h`;

    // Staff rostered to a team, NOT filtered by the header search box (the export
    // must always see every carer regardless of what's typed in the search bar).
    const exportStaffOf = (group) =>
        employeesWithRoster.filter((e) => e.rosterDetails?.group_id === group.id);

    // Employees offered in the export's "Employee" dropdown (one entry per
    // rostered carer, ordered by team then name).
    const exportEmployeeOptions = groups.flatMap((g) =>
        exportStaffOf(g).map((e) => ({ id: e.id, name: e.name }))
    );

    // The set of calendar dates covered by the chosen export period.
    //   day       → the selected day
    //   week      → the visible week (Mon–Sun)
    //   fourweek  → the 4 payroll weeks ending with the visible week (28 days)
    //   month     → the calendar month containing the selected day
    const exportDateRange = () => {
        if (exportPeriod === 'week') return weekDates;
        if (exportPeriod === 'fourweek') {
            const end = new Date(weekDates.length ? weekDates[6] : dailyDate);
            const start = new Date(end);
            start.setDate(start.getDate() - 27);
            const out = [];
            for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
            return out;
        }
        if (exportPeriod === 'month') {
            const start = new Date(dailyDate.getFullYear(), dailyDate.getMonth(), 1);
            const end = new Date(dailyDate.getFullYear(), dailyDate.getMonth() + 1, 0);
            const out = [];
            for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) out.push(new Date(d));
            return out;
        }
        return [dailyDate];
    };

    // The loaded week only carries that week's shifts, so multi-week periods need
    // the spanning weeks fetched and merged (one weekly call per Monday in range).
    const loadExportEmployees = async (dates) => {
        const weekKeys = Array.from(new Set(dates.map((d) => localDateKey(startOfWeek(d)))));
        const byId = {};
        for (const wk of weekKeys) {
            const res = await dutyRosterService.getDutyRoster(wk, 'weekly');
            if (!res.success) continue;
            (res.data.employees || []).forEach((e) => {
                if (!byId[e.id]) {
                    byId[e.id] = { ...e, shifts: [...(e.shifts || [])], unavailability: [...(e.unavailability || [])] };
                } else {
                    byId[e.id].shifts.push(...(e.shifts || []));
                    byId[e.id].unavailability.push(...(e.unavailability || []));
                }
            });
        }
        return Object.values(byId);
    };

    // Fetch the spanning weeks whenever the export modal is open on a multi-week
    // period; single day / week use the already-loaded data.
    useEffect(() => {
        if (!exportModalOpen || (exportPeriod !== 'fourweek' && exportPeriod !== 'month')) {
            setExportEmployees(null);
            return undefined;
        }
        let cancelled = false;
        (async () => {
            setExportLoading(true);
            try {
                const data = await loadExportEmployees(exportDateRange());
                if (!cancelled) setExportEmployees(data);
            } catch (_) {
                if (!cancelled) setExportEmployees([]);
            } finally {
                if (!cancelled) setExportLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exportModalOpen, exportPeriod, dailyDate, currentDate]);

    // Walk every rostered / worked record in the chosen period and classify it.
    // Only a CLOSED shift that matched the schedule (kind 'done') is payroll-ready;
    // live shifts, not-yet-started future shifts and records needing review
    // (differs / missed) are counted but excluded from payroll. Rest / leave days
    // are not payroll records at all. Returns the ready rows plus headline counts.
    const buildPayroll = () => {
        const dates = exportDateRange();
        const source = exportEmployees || employeesWithRoster;
        let recordsChecked = 0;
        let reviewCount = 0;   // differs / missed → needs manager review
        let pendingCount = 0;  // live (in progress) / future (not clocked in yet)
        const rows = [];
        groups.forEach((group) => {
            source
                .filter((e) => e.rosterDetails?.group_id === group.id)
                .filter((e) => exportEmployeeId === 'all' || String(e.id) === String(exportEmployeeId))
                .forEach((emp) => {
                    dates.forEach((date) => {
                        const dateStr = localDateKey(date);
                        const dayShift = findDayShift(emp, dateStr);
                        const working = parseDays(emp).includes(getDayName(date)) || !!dayShift;
                        if (!working) return; // rest day — not a payroll record
                        const st = getCellStatus(emp, date, dayShift);
                        if (st.kind === 'unavailable') return; // leave — not payable
                        recordsChecked += 1;
                        const isReady = st.kind === 'done' && dayShift && dayShift.end_time;
                        if (isReady) {
                            rows.push({
                                ref: empRef(emp),
                                name: emp.name,
                                team: group.name,
                                date,
                                payCode: EXPORT_PAY_CODE,
                                clockIn: fmtClock(dayShift.start_time),
                                clockOut: fmtClock(dayShift.end_time),
                                breakMins: dayShift.break_time_minutes || 0,
                                payableHours: Math.max(0, dayShift.worked_minutes || 0) / 60,
                                status: 'Matched',
                            });
                        } else if (st.kind === 'differs' || st.kind === 'missed') {
                            reviewCount += 1;
                        } else {
                            pendingCount += 1; // live / future
                        }
                    });
                });
        });
        const totalPayableHours = rows.reduce((t, r) => t + r.payableHours, 0);
        return {
            rows,
            recordsChecked,
            reviewCount,
            notIncluded: reviewCount + pendingCount,
            totalPayableHours,
        };
    };

    const downloadPayrollCsv = () => {
        const { rows } = buildPayroll();
        if (rows.length === 0) {
            toast.error('No payroll-ready rows to export');
            return;
        }
        const header = [
            'Employee Reference', 'Employee Name', 'Team', 'Work Date', 'Pay Code',
            'Clock In', 'Clock Out', 'Unpaid Break (min)', 'Payable Hours', 'Status',
        ];
        const csvEsc = (v) => {
            const s = String(v ?? '');
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const lines = [header.join(',')];
        rows.forEach((r) => {
            lines.push([
                r.ref, r.name, r.team, fmtUKDate(r.date), r.payCode,
                r.clockIn, r.clockOut, r.breakMins, r.payableHours.toFixed(2), r.status,
            ].map(csvEsc).join(','));
        });
        // BOM + CRLF so Excel opens the UK characters and line breaks cleanly.
        const csv = '﻿' + lines.join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const range = exportDateRange();
        const periodLabel = range.length > 1
            ? `${localDateKey(range[0])}_to_${localDateKey(range[range.length - 1])}`
            : localDateKey(range[0]);
        a.download = `payroll_hours_${periodLabel}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${rows.length} payroll row${rows.length === 1 ? '' : 's'}`);
        setExportModalOpen(false);
    };

    if (loading && groups.length === 0) {
        return <Loader text="Loading schedule..." />;
    }

    const dutyRosterCtx = {
        currentDate,
        setCurrentDate,
        weekDates,
        setWeekDates,
        groups,
        employeesWithRoster,
        allEmployees,
        loading,
        setLoading,
        publishLoading,
        setPublishLoading,
        isShiftDetailsModalOpen,
        setIsShiftDetailsModalOpen,
        rosterView,
        setRosterView,
        dailyDate,
        setDailyDate,
        selectedShiftDetail,
        setSelectedShiftDetail,
        employeeModal,
        setEmployeeModal,
        employeeUnavailability,
        setEmployeeUnavailability,
        unavailabilityLoading,
        setUnavailabilityLoading,
        unavailForm,
        setUnavailForm,
        unavailSaving,
        setUnavailSaving,
        searchTerm,
        setSearchTerm,
        collapsedTeams,
        setCollapsedTeams,
        colourKeyOpen,
        setColourKeyOpen,
        exportModalOpen,
        setExportModalOpen,
        exportPeriod,
        setExportPeriod,
        exportEmployeeId,
        setExportEmployeeId,
        manageTeamsOpen,
        setManageTeamsOpen,
        manageStaff,
        setManageStaff,
        manageMoves,
        setManageMoves,
        manageLoading,
        setManageLoading,
        manageSearch,
        setManageSearch,
        savingMoves,
        setSavingMoves,
        draggedMemberId,
        setDraggedMemberId,
        dragOverTeam,
        setDragOverTeam,
        coverModal,
        setCoverModal,
        coverForm,
        setCoverForm,
        coverStaff,
        setCoverStaff,
        coverStaffLoading,
        setCoverStaffLoading,
        savingCover,
        setSavingCover,
        renameModal,
        setRenameModal,
        editManagers,
        setEditManagers,
        editMembers,
        setEditMembers,
        editLoading,
        setEditLoading,
        savingRename,
        setSavingRename,
        reviewModal,
        setReviewModal,
        manualForm,
        setManualForm,
        absenceType,
        setAbsenceType,
        absenceNote,
        setAbsenceNote,
        reviewSaving,
        setReviewSaving,
        reviewListOpen,
        setReviewListOpen,
        navigate,
        user,
        isDomiciliaryManager,
        openExportModal,
        openManageTeams,
        closeManageTeams,
        setMemberTeam,
        submitManageTeams,
        addTeamOpen,
        setAddTeamOpen,
        newTeamName,
        setNewTeamName,
        savingTeam,
        openAddTeam,
        closeAddTeam,
        submitAddTeam,
        rosterTimes,
        openCoverForOff,
        openCoverPicker,
        onCoverEmployeeChange,
        closeCover,
        submitCover,
        openRenameTeam,
        closeRenameTeam,
        submitRenameTeam,
        deleteTeam,
        toggleTeamCollapse,
        jumpToNow,
        startOfWeek,
        formatDateObj,
        formatWeekRange,
        getDayName,
        fetchRoster,
        openEmployeeModal,
        closeEmployeeModal,
        submitUnavailability,
        removeUnavailability,
        isEmployeeUnavailableOn,
        getClockInStatus,
        getAttendanceMatch,
        handlePublish,
        removeDay,
        TONE,
        TEAM_COLORS,
        teamColor,
        parseDays,
        teamStaff,
        findDayShift,
        fmtClock,
        getCellStatus,
        openCell,
        EMP_COL,
        HOUR_PX,
        CARD_GAP,
        BOARD_W,
        hourLabels,
        laneBox,
        shortDays,
        collectReviews,
        reviewCountForTeam,
        renderWeekCell,
        renderDayRow,
        openReview,
        closeReview,
        refreshRoster,
        submitReviewShift,
        submitShiftEdit,
        saveReviewAbsence,
        EXPORT_PAY_CODE,
        empRef,
        fmtUKDate,
        fmtHours,
        exportStaffOf,
        exportEmployeeOptions,
        exportDateRange,
        exportLoading,
        buildPayroll,
        downloadPayrollCsv,
        // Draft / publish
        changes,
        handleDiscard,
        removeOneChange,
        draftModalOpen,
        setDraftModalOpen,
        publishModalOpen,
        setPublishModalOpen,
    };

    return (
        <DutyRosterProvider value={dutyRosterCtx}>
            <div className="w-full pb-20 font-sans">
                <RosterHeader />
                <RosterGrid />
                <EmployeeDetailsModal />
                <ShiftDetailsModal />
                <ReviewModal />
                <ReviewListModal />
                <ManageTeamsModal />
                <AddTeamModal />
                <CoverModal />
                <RenameTeamModal />
                <ExportPayrollModal />
                <PublishReviewModal />
            </div>
        </DutyRosterProvider>
    );
};

export default DutyRoster;
